import {logger} from '../utils/Logger';
import {isValidString} from '../utils/stringUtils';
import {isBoolean, isNotNullish, isNullish, isPlainObject} from '../utils/Utils';

import {CustomDataValues, sanitizeCustomDataValues} from './CustomDataValues';

export interface SourceMetadata {
  readonly title?: string;
  readonly videoId?: string;
  readonly cdnProvider?: string;
  readonly path?: string;
  readonly isLive?: boolean;
  readonly customData?: CustomDataValues;
}

export function hasValidSourceMetadataStructure(anything: unknown): anything is SourceMetadata | null | undefined {
  //  "nullish" is considered to be valid SourceMetadata, because we will then use an empty object as a default value
  if (isNullish(anything)) {
    return true;
  }

  // invalid, when value was defined, but not as a plain object
  if (!isPlainObject(anything as object)) {
    logger.errorMessageToUser('Invalid SourceMetadata: not a plain object');
    return false;
  }

  const maybeSourceMetadata = anything as SourceMetadata;

  if (isNotNullish(maybeSourceMetadata.customData) && !isPlainObject(maybeSourceMetadata.customData)) {
    logger.errorMessageToUser("Invalid SourceMetadata: 'customData' property is not a plain object");
    return false;
  }

  return true;
}

export function sanitizeSourceMetadata(sourceMetadata: SourceMetadata | null | undefined): SourceMetadata {
  //  "nullish" is considered to be valid SourceMetadata
  if (isNullish(sourceMetadata)) {
    // sanitize to be not "nullish"
    return {};
  }

  let result: SourceMetadata = {};
  if ('title' in sourceMetadata) {
    result = {
      ...result,
      title: isValidString(sourceMetadata.title) ? sourceMetadata.title : undefined,
    };
  }

  if ('videoId' in sourceMetadata) {
    result = {
      ...result,
      videoId: isValidString(sourceMetadata.videoId) ? sourceMetadata.videoId : undefined,
    };
  }

  if ('cdnProvider' in sourceMetadata) {
    result = {
      ...result,
      cdnProvider: isValidString(sourceMetadata.cdnProvider) ? sourceMetadata.cdnProvider : undefined,
    };
  }

  if ('path' in sourceMetadata) {
    result = {
      ...result,
      path: isValidString(sourceMetadata.path) ? sourceMetadata.path : undefined,
    };
  }

  if ('isLive' in sourceMetadata) {
    result = {
      ...result,
      isLive: isBoolean(sourceMetadata.isLive) ? sourceMetadata.isLive : undefined,
    };
  }

  if ('customData' in sourceMetadata) {
    result = {
      ...result,
      customData: isNotNullish(sourceMetadata.customData)
        ? sanitizeCustomDataValues(sourceMetadata.customData)
        : undefined,
    };
  }

  return result;
}
